import {useFormContext} from "react-hook-form";
import {RiLock2Line} from "react-icons/ri";

import {useCurrentUserContext} from "@/components/context/hooks.ts";
import {Divider} from "@/components/divider/Divider.tsx";
import {FileInputField} from "@/components/form/fields/FileInputField.tsx";
import {PasswordField} from "@/components/form/fields/PasswordField/PasswordField.tsx";
import {FormLabel} from "@/components/form/FormLabel.tsx";
import {TooltipIconRA} from "@/components/overlay/TooltipIconRA.tsx";
import {CredentialType} from "@/lib/types/credentials.ts";

import {CreateCredentialFormValues} from "./CredentialCreateForm.tsx";

interface CredentialFormFieldsProps {
  usedSecretProperties?: string[];
  hint?: string;
  disabled?: boolean;
}

const BasicCredentialFormFields = ({usedSecretProperties, disabled}: CredentialFormFieldsProps) => {
  const {
    register,
    formState: {errors},
  } = useFormContext<CreateCredentialFormValues>();
  const passwordPlaceholder = usedSecretProperties?.includes("password") ? "********" : "";
  return (
    <>
      <div className="form-group my-2">
        <FormLabel forId="username">User Name</FormLabel>
        <input id="username" disabled={disabled} className="form-control" type="text" {...register("username")} />
        {errors.username && <div className="invalid-feedback d-block">{errors.username?.message}</div>}
      </div>
      <PasswordField
        label={"Password"}
        disabled={disabled}
        placeholder={passwordPlaceholder}
        required
        name={"secret.password"}
        className={"form-group my-2"}
        labelIcon={RiLock2Line}
        hideMaskButtonNoValue={true}
      />
    </>
  );
};
const TokenCredentialFormFields = ({hint, usedSecretProperties, disabled}: CredentialFormFieldsProps) => {
  const tokenPlaceholder = usedSecretProperties?.includes("secret") ? `****${hint}` : "";
  return (
    <PasswordField
      label={"Token"}
      disabled={disabled}
      placeholder={tokenPlaceholder}
      required
      name={"secret.secret"}
      className={"form-group my-2"}
      labelIcon={RiLock2Line}
      hideMaskButtonNoValue={true}
    />
  );
};
const CertificateCredentialFormFields = ({hint, usedSecretProperties, disabled}: CredentialFormFieldsProps) => {
  const certificatePlaceholder = usedSecretProperties?.includes("certificate")
    ? hint
      ? `****${hint}`
      : "********"
    : "-----BEGIN CERTIFICATE-----";
  const certificateFilePlaceholder = usedSecretProperties?.includes("certificate") ? "********" : undefined;
  const keyPlaceholder = usedSecretProperties?.includes("key") ? "********" : undefined;
  const passphrasePlaceholder = usedSecretProperties?.includes("passphrase") ? "********" : "";
  const {
    register,
    formState: {errors},
  } = useFormContext<CreateCredentialFormValues>();
  return (
    <>
      <div className="form-group my-2">
        <FormLabel required forId="certificate_text" icon={RiLock2Line}>
          Add Certificate via Text
        </FormLabel>
        <textarea
          id="certificate_text"
          disabled={disabled}
          placeholder={certificatePlaceholder}
          className="form-control"
          {...register("secret.certificate")}
        />
        {errors.secret?.certificate && (
          <div className="invalid-feedback fw-semibold d-block">{errors.secret?.certificate?.message}</div>
        )}
      </div>

      <div className={"d-flex"}>
        <Divider />
        <div className={"text-black-50"}>OR</div>
        <Divider />
      </div>
      <FileInputField
        className={"form-group my-2"}
        disabled={disabled}
        labelIcon={RiLock2Line}
        label={"Upload Certificate File"}
        name={"secret.certificate_file"}
        fileTypes={".pem"}
        placeholder={certificateFilePlaceholder}
        required
      />
      <FileInputField
        className={"form-group my-2"}
        disabled={disabled}
        labelIcon={RiLock2Line}
        label={"Upload Key File"}
        name={"secret.key_file"}
        fileTypes={".pem"}
        placeholder={keyPlaceholder}
      />

      <PasswordField
        label={"Passphrase"}
        disabled={disabled}
        placeholder={passphrasePlaceholder}
        name={"secret.passphrase"}
        className={"form-group my-2"}
        labelIcon={RiLock2Line}
        hideMaskButtonNoValue={true}
      />
    </>
  );
};
const TOTPCredentialFormFields = ({hint, usedSecretProperties, disabled}: CredentialFormFieldsProps) => {
  const TotpPlaceholder = usedSecretProperties?.includes("secret") ? `****${hint}` : "";
  const periodPlaceholder = usedSecretProperties?.includes("period") ? "*******" : "";
  const numDigitsPlaceholder = usedSecretProperties?.includes("num_digits") ? "*******" : "";
  const {
    register,
    formState: {errors},
  } = useFormContext<CreateCredentialFormValues>();
  return (
    <>
      <PasswordField
        label={"TOTP Secret"}
        disabled={disabled}
        placeholder={TotpPlaceholder}
        required
        name={"secret.secret"}
        className={"form-group my-2"}
        labelIcon={RiLock2Line}
        hideMaskButtonNoValue={true}
      />
      <div className="form-group my-2">
        <FormLabel forId="period" required={true} icon={RiLock2Line}>
          TOTP Period
        </FormLabel>
        <input
          id="period"
          disabled={disabled}
          placeholder={periodPlaceholder}
          className="form-control"
          type="text"
          {...register("secret.period")}
        />
        {errors.secret?.period && <div className="invalid-feedback d-block">{errors.secret?.period?.message}</div>}
      </div>
      <div className="form-group my-2">
        <FormLabel forId="num_digits" required={true} icon={RiLock2Line}>
          TOTP Number of Digits
        </FormLabel>
        <input
          id="num_digits"
          disabled={disabled}
          placeholder={numDigitsPlaceholder}
          className="form-control"
          type="text"
          {...register("secret.num_digits")}
        />
        {errors.secret?.num_digits && (
          <div className="invalid-feedback d-block">{errors.secret?.num_digits?.message}</div>
        )}
      </div>
    </>
  );
};

interface CredentialTypeFormFieldsProps {
  credentialType: CredentialType;
  hint?: string;
  usedSecretProperties?: string[];
}

export const CredentialTypeFormFields = ({
  credentialType,
  hint,
  usedSecretProperties,
}: CredentialTypeFormFieldsProps) => {
  const multipleSecretFields = [CredentialType.CERTIFICATE, CredentialType.TOTP].includes(credentialType);
  const {user} = useCurrentUserContext();
  const disabled = user?.has_write_access === false;
  return (
    <>
      {multipleSecretFields && (
        <TooltipIconRA
          title={
            "To update a vault item credential please enter values for all fields as any field in the credential left empty while updating will be saved as blank values in the vault"
          }
        />
      )}
      {credentialType === CredentialType.BASIC && (
        <BasicCredentialFormFields usedSecretProperties={usedSecretProperties} disabled={disabled} />
      )}
      {credentialType === CredentialType.TOKEN && (
        <TokenCredentialFormFields hint={hint} usedSecretProperties={usedSecretProperties} disabled={disabled} />
      )}
      {credentialType === CredentialType.CERTIFICATE && (
        <CertificateCredentialFormFields hint={hint} usedSecretProperties={usedSecretProperties} disabled={disabled} />
      )}
      {credentialType === CredentialType.TOTP && (
        <TOTPCredentialFormFields hint={hint} usedSecretProperties={usedSecretProperties} disabled={disabled} />
      )}
    </>
  );
};
